/**
 * HousePrintView
 *
 * Full-screen overlay that shows a print-ready house list.
 * - One house per page (CSS page-break-after)
 * - Students sorted so friend groups appear together
 * - "Print / Save as PDF" button opens a new blank window containing only
 *   the serialised house pages, then calls window.print() there — so no
 *   app chrome ever appears in the print output.
 * - "Close" button dismisses the overlay
 *
 * Why new-window instead of window.print() on the main page?
 * The overlay is position:fixed inside a React portal, and the scroll
 * container clips content below the fold. Getting all pages to render
 * correctly via @media print or beforeprint DOM surgery is fragile across
 * browsers. Serialising the already-rendered innerHTML into a fresh
 * document sidesteps all of that cleanly.
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, Users } from 'lucide-react';
import { Button } from '../ui/button';

// ── Colour palette for friend groups (cycles if > 8 groups) ────────────────
const GROUP_COLOURS = [
  { bg: '#FEF3C7', border: '#F59E0B', label: '#92400E' },  // amber
  { bg: '#DBEAFE', border: '#3B82F6', label: '#1E40AF' },  // blue
  { bg: '#D1FAE5', border: '#10B981', label: '#065F46' },  // green
  { bg: '#EDE9FE', border: '#8B5CF6', label: '#4C1D95' },  // violet
  { bg: '#FCE7F3', border: '#EC4899', label: '#831843' },  // pink
  { bg: '#FFEDD5', border: '#F97316', label: '#7C2D12' },  // orange
  { bg: '#CFFAFE', border: '#06B6D4', label: '#164E63' },  // cyan
  { bg: '#F0FDF4', border: '#22C55E', label: '#14532D' },  // lime
];

function groupColour(groupId) {
  if (!groupId) return null;
  return GROUP_COLOURS[(groupId - 1) % GROUP_COLOURS.length];
}

// ── Single student row ──────────────────────────────────────────────────────
function StudentRow({ student, isFirstInGroup, isLastInGroup }) {
  const colour = groupColour(student.friend_group_id);
  const isGrouped = !!colour;

  // Cross-house friends (friends not in this house)
  const crossHouseFriends = student.friends?.filter((f) => !f.in_this_house) ?? [];

  const rowStyle = isGrouped
    ? {
        backgroundColor: colour.bg,
        borderLeft: `3px solid ${colour.border}`,
        ...(isFirstInGroup ? { borderTop: `1px solid ${colour.border}` } : {}),
        ...(isLastInGroup  ? { borderBottom: `1px solid ${colour.border}` } : {}),
      }
    : {};

  return (
    <tr style={rowStyle}>
      <td style={{ padding: '4px 8px', fontSize: '11px', color: '#374151', fontFamily: 'monospace' }}>
        {student.student_id}
      </td>
      <td style={{ padding: '4px 8px', fontSize: '12px', fontWeight: 500, color: '#111827' }}>
        {student.full_name}
        {student.is_cinnire && (
          <span style={{
            fontSize: '9px',
            fontWeight: 600,
            color: '#92400E',
            backgroundColor: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: 4,
            padding: '1px 5px',
            marginLeft: 6,
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
          }}>
            Cinnire
          </span>
        )}
        {crossHouseFriends.length > 0 && (
          <span style={{ fontSize: '10px', color: '#6B7280', marginLeft: 6 }}>
            {crossHouseFriends.map((f) => `↔ ${f.full_name}${f.house_name ? ` (#${f.house_id})` : ''}`).join(', ')}
          </span>
        )}
      </td>
      <td style={{ padding: '4px 8px', fontSize: '11px', color: '#6B7280' }}>
        {student.school_year}
      </td>
      {isGrouped && isFirstInGroup && (
        <td
          rowSpan={student._groupSize}
          style={{
            padding: '4px 8px',
            fontSize: '10px',
            fontWeight: 600,
            color: colour.label,
            textAlign: 'center',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
          }}
        >
          <Users size={11} style={{ display: 'inline', marginRight: 3 }} />
          Grúpa Cairde {student.friend_group_id}
        </td>
      )}
      {(!isGrouped || !isFirstInGroup) && <td />}
    </tr>
  );
}

// ── Single house page ───────────────────────────────────────────────────────
function HousePage({ house, isLast }) {
  // Annotate each student with _groupSize so the first row can rowSpan
  const groupSizes = {};
  house.students.forEach((s) => {
    if (s.friend_group_id) {
      groupSizes[s.friend_group_id] = (groupSizes[s.friend_group_id] ?? 0) + 1;
    }
  });

  const groupFirstSeen = {};
  const annotated = house.students.map((s) => {
    const gid = s.friend_group_id;
    if (!gid) return { ...s, _isFirst: false, _isLast: false };
    const isFirst = !groupFirstSeen[gid];
    if (isFirst) groupFirstSeen[gid] = true;
    return { ...s, _groupSize: groupSizes[gid], _isFirst: isFirst };
  });

  // Mark last in each group
  const groupLastSeen = {};
  const fullyAnnotated = [...annotated].reverse().map((s) => {
    const gid = s.friend_group_id;
    if (!gid) return s;
    const isLast = !groupLastSeen[gid];
    if (isLast) groupLastSeen[gid] = true;
    return { ...s, _isLast: isLast };
  }).reverse();

  const genderLabel = house.gender_designation === 'M' ? 'Buachaillí' : 'Cailíní';

  return (
    <div
      style={{
        pageBreakAfter: isLast ? 'auto' : 'always',
        breakAfter: isLast ? 'auto' : 'page',
        padding: '24px 32px',
        minHeight: '100%',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* House header */}
      <div style={{ borderBottom: '2px solid #E5E7EB', paddingBottom: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>
              {house.name}
            </h2>
            <span style={{ fontSize: 12, color: '#6B7280', marginRight: 12 }}>
              Teach #{house.id} · {genderLabel}
            </span>
          </div>
          <div style={{ textAlign: 'right', fontSize: 12, color: '#374151' }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {house.current_occupancy} / {house.max_capacity} mac léinn
            </div>
          </div>
        </div>

        {/* Bean an tí details */}
        {(house.owner_name || house.owner_phone || house.owner_email) && (
          <div style={{ marginTop: 8, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: '#374151' }}>
            {house.owner_name && (
              <span>
                <strong>Bean an Tí:</strong> {house.owner_name}
              </span>
            )}
            {house.owner_phone && (
              <span>
                <strong>📞</strong> {house.owner_phone}
              </span>
            )}
            {house.owner_email && (
              <span>
                <strong>✉</strong> {house.owner_email}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Student table */}
      {fullyAnnotated.length === 0 ? (
        <p style={{ color: '#9CA3AF', fontSize: 12, fontStyle: 'italic' }}>Níl aon mhac léinn sa teach seo fós.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600, width: 70 }}>
                ID
              </th>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600 }}>
                Ainm
              </th>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600, width: 90 }}>
                Bliain
              </th>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600, width: 80 }}>
                Cairde
              </th>
            </tr>
          </thead>
          <tbody>
            {fullyAnnotated.map((student) => (
              <StudentRow
                key={student.student_id}
                student={student}
                isFirstInGroup={student._isFirst}
                isLastInGroup={student._isLast}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Cúntóirí section */}
      {house.cuntoirs?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#0F766E',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 6,
            paddingBottom: 4,
            borderBottom: '1px solid #99F6E4',
          }}>
            Cúntóirí ({house.cuntoirs.length})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {house.cuntoirs.map((c, i) => (
                <tr
                  key={i}
                  style={{ backgroundColor: '#F0FDFA', borderBottom: '1px solid #CCFBF1' }}
                >
                  <td style={{ padding: '4px 8px', fontSize: 12, fontWeight: 500, color: '#134E4A' }}>
                    {c.full_name}
                  </td>
                  <td style={{ padding: '4px 8px', fontSize: 11, color: '#0F766E' }}>
                    {c.gender === 'M' ? 'Buachaill' : 'Cailín'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export function HousePrintView({ data, onClose }) {
  const { course, houses, generated_at } = data;
  const scrollRef = useRef(null);

  // Format the generated timestamp
  const generatedAt = generated_at
    ? new Date(generated_at).toLocaleString('ga-IE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  // Prevent body scroll while the overlay is open.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handlePrint = () => {
    const scrollEl = scrollRef.current;

    // Should never happen, but guard defensively.
    if (!scrollEl) { window.print(); return; }

    // Serialise the already-rendered content (inline styles, SVG icons, etc.)
    // into a fresh blank window, then trigger print there.  This is the
    // canonical strategy: only the house pages land in the print output —
    // no overlay chrome, no React app, no clipping from overflow:hidden.
    const bodyHtml = scrollEl.innerHTML;
    const title    = course?.name ?? 'Tithe';

    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      // Popup blocked (rare from a click handler, but handle it).
      // Last-resort: print the main page as-is — output will include the
      // overlay but is still better than silently doing nothing.
      window.print();
      return;
    }

    win.document.write(`<!DOCTYPE html>
<html lang="ga">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0; padding: 0;
      font-family: system-ui, -apple-system, sans-serif;
      background: white;
    }
    /* Strip screen-only card chrome */
    .house-card {
      box-shadow: none !important;
      border-radius: 0 !important;
      margin-bottom: 0 !important;
      overflow: visible !important;
    }
  </style>
</head>
<body>
${bodyHtml}
<script>window.print();<\/script>
</body>
</html>`);
    win.document.close();
  };

  return createPortal(
    <>
      {/* Full-screen overlay — mounted directly on body via createPortal */}
      <div
        className="house-print-overlay"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: '#f3f4f6',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Toolbar — not included in the print window (only scrollRef innerHTML is serialised) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px',
            backgroundColor: '#1F2937',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <div>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{course?.name}</span>
            <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 12 }}>
              {houses.length} teach · {houses.reduce((n, h) => n + h.current_occupancy, 0)} mac léinn
            </span>
            {generatedAt && (
              <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 12 }}>{generatedAt}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-1.5" />
              Priontáil / Save as PDF
            </Button>
            <Button size="sm" variant="outline" onClick={onClose}
              style={{ color: 'white', borderColor: '#4B5563', backgroundColor: 'transparent' }}
            >
              <X className="h-4 w-4 mr-1" />
              Dún
            </Button>
          </div>
        </div>

        {/* Scrollable preview */}
        <div
          ref={scrollRef}
          className="house-print-scroll"
          style={{ flex: 1, overflowY: 'auto', padding: 24 }}
        >
          {houses.map((house, idx) => (
            <div
              key={house.uuid}
              className="house-card"
              style={{
                backgroundColor: 'white',
                borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                marginBottom: idx < houses.length - 1 ? 24 : 0,
              }}
            >
              <HousePage house={house} isLast={idx === houses.length - 1} />
            </div>
          ))}

          {houses.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: '#6B7280' }}>
              Níl aon teach ann don chúrsa seo fós.
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
